import React, { useState } from 'react';
import { X, PlusCircle, AlertCircle } from 'lucide-react';
import { helpService } from '../helpService';
import { HelpCategory, HelpFaq } from '../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  categories: HelpCategory[];
  organizations: Array<{ id: string; organization_code: string; legal_name: string }>;
  currentOrgId: string;
  onCreated: (faq: HelpFaq) => void;
  userDisplayName: string;
}

export const CreateFaqModal: React.FC<Props> = ({
  isOpen,
  onClose,
  categories,
  organizations,
  currentOrgId,
  onCreated,
  userDisplayName,
}) => {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [categoryId, setCategoryId] = useState(categories[0]?.id || '');
  const [tags, setTags] = useState('');
  const [scopeType, setScopeType] = useState<'GLOBAL' | 'SINGLE_ORG'>('GLOBAL');
  const [targetOrgId, setTargetOrgId] = useState(currentOrgId);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim() || !answer.trim()) {
      setError('Question and answer fields are required.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const isGlobal = scopeType === 'GLOBAL';
      const faq = await helpService.createFaq({
        question: question.trim(),
        answer: answer.trim(),
        category_id: categoryId || null,
        tags: tags.trim() || null,
        is_global: isGlobal,
        organization_id: isGlobal ? null : targetOrgId,
        created_by_name: userDisplayName,
      });

      onCreated(faq);
      onClose();
    } catch (err: any) {
      setError(err.message || 'An error occurred while publishing the FAQ.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-lg">
        <div className="modal-header">
          <div className="modal-header-content">
            <h3>Add New FAQ & Knowledge Base Article</h3>
            <p>Publish guidance material across all organizations or target a specific organization</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="modal-close-btn"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body space-y-4">
            {error && (
              <div
                style={{
                  padding: '12px 16px',
                  background: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  color: '#991b1b',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            {/* Scope selection: Global vs Single Org */}
            <div
              style={{
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                padding: '16px',
                borderRadius: '8px',
              }}
            >
              <label style={{ fontWeight: 600, fontSize: '13px', color: '#166534', display: 'block', marginBottom: '8px' }}>
                Publishing Scope (Organization Targeting) *
              </label>
              <div style={{ display: 'flex', gap: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}>
                  <input
                    type="radio"
                    name="scopeType"
                    checked={scopeType === 'GLOBAL'}
                    onChange={() => setScopeType('GLOBAL')}
                  />
                  <span>🌐 <strong>All Organizations (Global System Guide)</strong></span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}>
                  <input
                    type="radio"
                    name="scopeType"
                    checked={scopeType === 'SINGLE_ORG'}
                    onChange={() => setScopeType('SINGLE_ORG')}
                  />
                  <span>🏢 <strong>Specific Organization (Local SOP)</strong></span>
                </label>
              </div>

              {scopeType === 'SINGLE_ORG' && (
                <div style={{ marginTop: '12px' }}>
                  <label style={{ fontSize: '12px', color: '#334155', display: 'block', marginBottom: '4px' }}>
                    Select Target Organization:
                  </label>
                  <select
                    value={targetOrgId}
                    onChange={(e) => setTargetOrgId(e.target.value)}
                    style={{ width: '100%', maxWidth: '400px' }}
                  >
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.legal_name} ({org.organization_code})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="form-field">
              <label>Question / Title *</label>
              <input
                type="text"
                required
                placeholder="e.g. Who must be contacted upon detecting hazardous materials in the hangar?"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-field">
                <label>Category</label>
                <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                  <option value="">Select Category (Optional)</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title} {c.is_global ? '(Global)' : '(Org)'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label>Tags (Comma separated)</label>
                <input
                  type="text"
                  placeholder="e.g. hazmat, safety, emergency, duty manager"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                />
              </div>
            </div>

            <div className="form-field">
              <label>Answer / Solution Details *</label>
              <textarea
                required
                rows={6}
                placeholder="Required actions, contact information, relevant regulations and procedures..."
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                  fontFamily: 'inherit',
                }}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', width: 'auto' }}
            >
              <PlusCircle size={16} />
              {submitting ? 'Publishing...' : 'Publish to Guide'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
